export type Art = 'UMLEITUNG' | 'SPERRUNG' | 'HALTESTELLE' | 'VERANSTALTUNG' | 'SONSTIGES'
export type Status = 'ZU_PRUEFEN' | 'AKTIV' | 'ZURUECKGEZOGEN'

export interface Verkehrsmeldung {
  id: number
  nummer: string | null
  herausgeber: string | null
  art: Art
  titel: string
  ort: string | null
  anlass: string | null
  gueltigVon: string
  gueltigBis: string | null
  ersterBetriebstag: string
  letzterBetriebstag: string | null
  alleLinien: boolean
  linien: string[]
  gesperrteStrasse: string | null
  umleitungsstrecke: string | null
  entfallendeHaltestellen: string | null
  ersatzhaltestellen: string | null
  besonderheiten: string | null
  wichtig: boolean
  status: Status
  quelle: 'HAND' | 'UPLOAD' | 'EMAIL'
  auslesung: 'KEINE' | 'AUSSTEHEND' | 'ERLEDIGT' | 'GESCHEITERT'
  dateiname: string | null
  eingangAbsender: string | null
  eingangBetreff: string | null
  eingangAm: string | null
  kiModell: string | null
  kiDauerMs: number | null
  kiHinweis: string | null
  aktiviertAm: string | null
  benachrichtigt: number | null
  erstelltAm: string
}

export interface BetroffenerKurs {
  kursnummer: string
  linien: string[]
  fahrerId: number | null
  fahrerName: string | null
}

export interface Tageslage {
  betriebstag: string
  zuPruefen: number
  aktiv: { meldung: Verkehrsmeldung; kurse: BetroffenerKurs[] }[]
}

export const ARTEN: Record<Art, string> = {
  UMLEITUNG: 'Umleitung',
  SPERRUNG: 'Sperrung',
  HALTESTELLE: 'Haltestelle',
  VERANSTALTUNG: 'Veranstaltung',
  SONSTIGES: 'Sonstiges',
}

export const QUELLEN: Record<Verkehrsmeldung['quelle'], string> = {
  HAND: 'von Hand',
  UPLOAD: 'PDF-Upload',
  EMAIL: 'Posteingang',
}

/**
 * Der laufende Betriebstag als JJJJ-MM-TT in Ortszeit.
 *
 * Nicht `toISOString()`: Das rechnet in UTC, und zwischen Mitternacht und zwei Uhr stünde
 * dann der Vortag im Feld. Vor 04:00 gilt ohnehin noch der Vortag — der Betriebstag
 * wechselt erst dann.
 */
export function betriebstagHeute(): string {
  const d = new Date()
  if (d.getHours() < 4) d.setDate(d.getDate() - 1)
  return ortsdatum(d)
}

export function ortsdatum(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const t = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${t}`
}

export function verschiebe(datum: string, tage: number): string {
  const d = new Date(datum + 'T12:00:00')
  d.setDate(d.getDate() + tage)
  return ortsdatum(d)
}

function datum(iso: string): string {
  const [j, m, t] = iso.slice(0, 10).split('-')
  return `${t}.${m}.${j}`
}

/** „24.09.2026 – 22.12.2027", Uhrzeiten nur, wo sie etwas sagen. */
export function zeitraum(m: Verkehrsmeldung): string {
  const vonZeit = m.gueltigVon.slice(11, 16)
  const von = datum(m.gueltigVon) + (vonZeit && vonZeit !== '00:00' ? ` ${vonZeit}` : '')
  if (!m.gueltigBis) return `ab ${von}, bis auf Weiteres`
  const bisZeit = m.gueltigBis.slice(11, 16)
  // Ein Ende um 04:00 schließt den Betriebstag ab — gezeigt wird der Tag selbst.
  const bis =
    bisZeit === '04:00' && m.letzterBetriebstag
      ? datum(m.letzterBetriebstag)
      : datum(m.gueltigBis) + (bisZeit && bisZeit !== '00:00' ? ` ${bisZeit}` : '')
  return von === bis ? von : `${von} – ${bis}`
}

export function linienText(m: Verkehrsmeldung): string {
  if (m.alleLinien) return 'alle Linien'
  if (m.linien.length === 0) return 'keine Linie'
  return (m.linien.length === 1 ? 'Linie ' : 'Linien ') + m.linien.join(', ')
}
